/**
 * TasksPage Component
 * Main page for viewing and managing all tasks
 * 
 * FINLIST SSOT: Integrated with CategoryEnvelopeContext for budget envelope data
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  ListTodo,
  Calendar,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  Filter,
  Loader2,
  Clock,
  Sparkles,
  Link as LinkIcon,
  CalendarDays,
  LayoutList,
  GripVertical,
  CheckSquare,
  Square,
  Trash2,
  Archive,
  X,
  Keyboard,
  Bell,
  PiggyBank,
  Wallet,
  Search,
  ArrowUpDown,
  Undo2,
  Tag,
  Paperclip,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import DashboardHeader from '@/components/DashboardHeader';
import BottomNavigation from '@/components/BottomNavigation';
import TaskCard from '@/components/FinTasks/TaskCard';
import SwipeableTaskCard, { useIsTouchDevice } from '@/components/FinTasks/SwipeableTaskCard';
import AddTaskModal from '@/components/FinTasks/AddTaskModal';
import EditTaskModal from '@/components/FinTasks/EditTaskModal';
import DeleteTaskConfirmModal from '@/components/FinTasks/DeleteTaskConfirmModal';
import TaskDetailModal from '@/components/FinTasks/TaskDetailModal';
import TaskTemplatesModal from '@/components/FinTasks/TaskTemplatesModal';
import RecordTransactionModal from '@/components/FinTasks/RecordTransactionModal';
import NotificationPermissionBanner from '@/components/NotificationPermissionBanner';
// Phase 5: Templates & Suggestions Engine
import QuickAddTemplates from '@/components/FinTasks/QuickAddTemplates';
import SuggestedTasksWidget from '@/components/FinTasks/SuggestedTasksWidget';
// Phase 6: Calendar View
import CalendarView from '@/components/FinTasks/CalendarView';
import TaskCalendarPopover from '@/components/FinTasks/TaskCalendarPopover';
// Phase 7: Drag and Drop Reordering
import DraggableTaskList from '@/components/FinTasks/DraggableTaskList';
// Phase 7: Keyboard Shortcuts
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import KeyboardShortcutsModal from '@/components/FinTasks/KeyboardShortcutsModal';
import { useTasks } from '@/hooks/useTasks';
import { useTodoMeta } from '@/hooks/useTodoMeta';
import { useTodoLists } from '@/hooks/useTodoLists';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/config/firebase';
import { formatCurrency } from '@/lib/utils';
import toast from 'react-hot-toast';
import { prioritiseTasks, tasksFromSpending } from '@/services/finTasksAIService';
// FINLIST SSOT: Import for real-time envelope updates and budget sync
import { useCategoryEnvelope } from '@/context/CategoryEnvelopeContext';

const filters = [
  { id: 'all', label: 'All Tasks', shortLabel: 'All', icon: ListTodo },
  { id: 'today', label: 'Today', shortLabel: 'Today', icon: Calendar },
  { id: 'upcoming', label: 'Upcoming', shortLabel: 'Soon', icon: Calendar },
  { id: 'overdue', label: 'Overdue', shortLabel: 'Late', icon: AlertCircle },
  { id: 'completed', label: 'Completed', shortLabel: 'Done', icon: CheckCircle2 },
  { id: 'archived', label: 'Archived', shortLabel: 'Archive', icon: Archive },
  { id: 'financial', label: 'Financial', shortLabel: 'Money', icon: DollarSign },
  { id: 'savings', label: 'Savings & Goals', shortLabel: 'Goals', icon: PiggyBank }
];

// Beautiful color schemes for each filter
const filterColors = {
  all: {
    active: 'bg-gradient-to-r from-slate-600 to-slate-800 text-white shadow-lg shadow-slate-500/30 border-slate-500',
    inactive: 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700 hover:shadow-md'
  },
  today: {
    active: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30 border-amber-400',
    inactive: 'bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-700 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:shadow-md'
  },
  upcoming: {
    active: 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg shadow-cyan-500/30 border-cyan-400',
    inactive: 'bg-white dark:bg-slate-800 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-700 hover:border-cyan-400 dark:hover:border-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:shadow-md'
  },
  overdue: {
    active: 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-500/30 border-red-400',
    inactive: 'bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 border-red-200 dark:border-red-700 hover:border-red-400 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 hover:shadow-md'
  },
  completed: {
    active: 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/30 border-emerald-400',
    inactive: 'bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700 hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:shadow-md'
  },
  archived: {
    active: 'bg-gradient-to-r from-gray-500 to-zinc-600 text-white shadow-lg shadow-gray-500/30 border-gray-400',
    inactive: 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 hover:shadow-md'
  },
  financial: {
    active: 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg shadow-violet-500/30 border-violet-400',
    inactive: 'bg-white dark:bg-slate-800 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-700 hover:border-violet-400 dark:hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:shadow-md'
  },
  savings: {
    active: 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-500/30 border-teal-400',
    inactive: 'bg-white dark:bg-slate-800 text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-700 hover:border-teal-400 dark:hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:shadow-md'
  }
};

export default function TasksPage() {
  const { user, currency: userCurrency } = useAuth();
  const currency = userCurrency || 'GBP';
  
  // Phase 3: Detect touch device for swipe gestures
  const isTouchDevice = useIsTouchDevice();
  
  // FINLIST SSOT: Access Budget envelopes for budget-linked task metrics
  const { envelopes, refreshEnvelopes } = useCategoryEnvelope();
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [taskToView, setTaskToView] = useState(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [defaultListId, setDefaultListId] = useState(null);
  // Phase 4 AI assistance state (non-mutative)
  const [aiOrdering, setAiOrdering] = useState([]); // [{taskId, priorityScore, reason}]
  const [aiOrderingLoading, setAiOrderingLoading] = useState(false);
  const [showAIOrderingPanel, setShowAIOrderingPanel] = useState(false);
  const [useAIOrderView, setUseAIOrderView] = useState(false);
  const [spendingDrafts, setSpendingDrafts] = useState([]); // draft suggestions
  const [spendingDraftsLoading, setSpendingDraftsLoading] = useState(false);
  const [showSpendingDraftsPanel, setShowSpendingDraftsPanel] = useState(false);
  // Financial task → Budget integration: Record completed task as transaction
  const [showRecordTransactionModal, setShowRecordTransactionModal] = useState(false);
  const [taskToRecord, setTaskToRecord] = useState(null);
  
  // Phase 6: View mode (list vs calendar)
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [calendarPopoverTasks, setCalendarPopoverTasks] = useState([]);
  const [showCalendarPopover, setShowCalendarPopover] = useState(false);
  const [prefilledTaskDate, setPrefilledTaskDate] = useState(null); // Pre-fill date when adding from calendar
  
  // Phase 7: Drag mode for reordering
  const [isDragMode, setIsDragMode] = useState(false);
  
  // Phase 7: Bulk selection mode
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  
  // Filter pills scroll navigation (mobile)
  const filterScrollRef = useRef(null);
  const [showFilterLeftArrow, setShowFilterLeftArrow] = useState(false);
  const [showFilterRightArrow, setShowFilterRightArrow] = useState(true);
  
  // Stats panel visibility
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  
  // Phase 8: Enhanced UX Features
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default'); // 'default' | 'dueDate' | 'priority' | 'created' | 'alphabetical'
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [undoAction, setUndoAction] = useState(null); // { type, task, timeoutId }
  
  const { tasks, loading, toggleTask, addTask, editTask, removeTask, archiveTaskItem, unarchiveTaskItem, refetch, reorderTasks } = useTasks(null, activeFilter);
  const { stats, loading: statsLoading } = useTodoMeta();
  const { lists, loading: listsLoading, addList } = useTodoLists();

  // FINLIST: Compute budget-linked task metrics
  const budgetLinkedMetrics = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return { linkedCount: 0, totalBudgetImpact: 0 };
    }
    
    // Count tasks that have a linked envelope
    const linkedTasks = tasks.filter(t => t.linkedEnvelopeId);
    const linkedCount = linkedTasks.length;
    
    // Calculate total budget impact from financial tasks with estimated amounts
    const financialTasks = tasks.filter(t => t.isFinancialTask && t.estimatedAmount > 0);
    const totalBudgetImpact = financialTasks.reduce((sum, t) => sum + (parseFloat(t.estimatedAmount) || 0), 0);
    
    return { linkedCount, totalBudgetImpact };
  }, [tasks]);

  // Phase 8.2: Calculate task counts for each filter
  const filterCounts = useMemo(() => {
    if (!tasks || tasks.length === 0) return {};
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const allNonArchived = tasks.filter(t => t.status !== 'archived');
    const todayTasks = tasks.filter(t => {
      if (!t.dueDate || t.status === 'done' || t.status === 'archived') return false;
      const dueDate = new Date(t.dueDate);
      return dueDate >= today && dueDate < tomorrow;
    });
    const upcomingTasks = tasks.filter(t => {
      if (!t.dueDate || t.status === 'done' || t.status === 'archived') return false;
      const dueDate = new Date(t.dueDate);
      return dueDate >= tomorrow;
    });
    // Overdue: task's due date/time is before NOW (not just before start of today)
    const overdueTasks = tasks.filter(t => {
      if (!t.dueDate || t.status === 'done' || t.status === 'archived') return false;
      const dueDate = new Date(t.dueDate);
      return dueDate < now;
    });
    const completedTasks = tasks.filter(t => t.status === 'done');
    const archivedTasks = tasks.filter(t => t.status === 'archived');
    const financialTasks = tasks.filter(t => t.isFinancialTask === true && t.status !== 'archived');
    const savingsTasks = tasks.filter(t => t.goalId && t.status !== 'archived');
    
    return {
      all: allNonArchived.length,
      today: todayTasks.length,
      upcoming: upcomingTasks.length,
      overdue: overdueTasks.length,
      completed: completedTasks.length,
      archived: archivedTasks.length,
      financial: financialTasks.length,
      savings: savingsTasks.length
    };
  }, [tasks]);

  // Filter pills scroll navigation
  const checkFilterScrollPosition = useCallback(() => {
    const container = filterScrollRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowFilterLeftArrow(scrollLeft > 10);
    setShowFilterRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    const container = filterScrollRef.current;
    if (container) {
      checkFilterScrollPosition();
      container.addEventListener('scroll', checkFilterScrollPosition);
      window.addEventListener('resize', checkFilterScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkFilterScrollPosition);
        window.removeEventListener('resize', checkFilterScrollPosition);
      };
    }
  }, [checkFilterScrollPosition]);

  const scrollFilters = useCallback((direction) => {
    const container = filterScrollRef.current;
    if (!container) return;
    
    const scrollAmount = 120;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }, []);

  // Deep-link focus: Handle ?focus=taskId from notification clicks
  const [focusedTaskId, setFocusedTaskId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get('focus');
    if (focusId && tasks && tasks.length > 0) {
      // Find the task to focus
      const taskToFocus = tasks.find(t => t.id === focusId);
      if (taskToFocus) {
        console.log('[TasksPage] Focusing task from notification:', focusId);
        setFocusedTaskId(focusId);
        // Open task detail modal
        setTaskToView(taskToFocus);
        setShowDetailModal(true);
        // Clear the focus param from URL without reload
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        // Clear focus highlight after 3 seconds
        setTimeout(() => setFocusedTaskId(null), 3000);
      }
    }
  }, [tasks]);

  // Phase 8.4: Calculate today's completion progress
  const todayProgress = useMemo(() => {
    if (!tasks || tasks.length === 0) return { completed: 0, total: 0, percentage: 0 };
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const todayTasks = tasks.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = new Date(t.dueDate);
      return dueDate >= today && dueDate < tomorrow;
    });
    
    const completed = todayTasks.filter(t => t.status === 'done').length;
    const total = todayTasks.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { completed, total, percentage };
  }, [tasks]);

  // Phase 8.1 & 8.5: Filter by search and apply sort
  const sortedTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    
    // First, apply search filter
    let filtered = tasks;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = tasks.filter(t => 
        t.title?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // In drag mode, sort by the order field (user's custom order)
    if (isDragMode) {
      return [...filtered].sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
    }
    
    // Apply selected sort option
    return [...filtered].sort((a, b) => {
      const aCompleted = a.status === 'done';
      const bCompleted = b.status === 'done';
      
      // Completed tasks always go to the bottom (unless sorting by completion)
      if (sortBy !== 'alphabetical') {
        if (aCompleted && !bCompleted) return 1;
        if (!aCompleted && bCompleted) return -1;
      }
      
      switch (sortBy) {
        case 'dueDate':
          if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
          if (a.dueDate && !b.dueDate) return -1;
          if (!a.dueDate && b.dueDate) return 1;
          return 0;
          
        case 'priority': {
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          const aPriority = priorityOrder[a.priority] ?? 1;
          const bPriority = priorityOrder[b.priority] ?? 1;
          return aPriority - bPriority;
        }
          
        case 'created':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          
        case 'alphabetical':
          return (a.title || '').localeCompare(b.title || '');
          
        default: // 'default'
          // Due date first
          if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
          if (a.dueDate && !b.dueDate) return -1;
          if (!a.dueDate && b.dueDate) return 1;
          // Then by creation date
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });
  }, [tasks, isDragMode, searchQuery, sortBy]);

  // Refresh tasks and envelopes when page becomes visible again
  // This ensures refunds, unbought updates, and budget changes are reflected
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[TasksPage] Page visible, refreshing tasks and envelope data...');
        refetch();
        refreshEnvelopes?.(); // FINLIST: Also refresh budget envelope data
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch, refreshEnvelopes]);

  // Get or create default list ID
  useEffect(() => {
    const getDefaultList = async () => {
      if (lists && lists.length > 0) {
        const defaultList = lists.find(l => l.isDefault) || lists[0];
        setDefaultListId(defaultList.id);
      } else if (lists && lists.length === 0 && user?.uid && !listsLoading) {
        // No lists exist, create default one
        try {
          const newList = await addList({ title: 'My Tasks', isDefault: true });
          setDefaultListId(newList.id);
        } catch (error) {
          console.error('Error creating default list:', error);
        }
      }
    };

    getDefaultList();
  }, [lists, listsLoading, user?.uid, addList]);

  // Phase 8.3: Undo handler
  const handleUndo = useCallback(async () => {
    if (!undoAction) return;
    
    const { type, task, timeoutId } = undoAction;
    clearTimeout(timeoutId);
    
    try {
      if (type === 'complete') {
        await toggleTask(task.listId, task.id);
        toast.success('Task restored!');
      } else if (type === 'archive') {
        await unarchiveTaskItem(task.listId, task.id);
        toast.success('Task unarchived!');
      }
      refetch();
    } catch (error) {
      toast.error('Failed to undo action');
    }
    
    setUndoAction(null);
  }, [undoAction, toggleTask, unarchiveTaskItem, refetch]);

  const handleToggle = async (task) => {
    try {
      await toggleTask(task.listId, task.id);
      const wasCompleted = task.status === 'done';
      
      if (!wasCompleted) {
        // Task is being marked as complete - show undo toast
        // Clear any existing undo action
        if (undoAction?.timeoutId) {
          clearTimeout(undoAction.timeoutId);
        }
        
        const timeoutId = setTimeout(() => {
          setUndoAction(null);
        }, 5000);
        
        setUndoAction({ type: 'complete', task, timeoutId });
        
        toast((t) => (
          <div className="flex items-center gap-3">
            <span>Task completed! 🎉</span>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                handleUndo();
              }}
              className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
            >
              Undo
            </button>
          </div>
        ), {
          duration: 5000,
          style: {
            background: '#10b981',
            color: '#fff',
          }
        });
        
        // Financial Task → Budget Integration:
        if (task.isFinancialTask && task.estimatedAmount && task.estimatedAmount > 0) {
          setTimeout(() => {
            setTaskToRecord(task);
            setShowRecordTransactionModal(true);
          }, 300);
        }
        
        // Check if all today's tasks are now complete
        const todayTasks = tasks.filter(t => {
          if (!t.dueDate) return false;
          const dueDate = new Date(t.dueDate);
          const today = new Date();
          dueDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          return dueDate.getTime() === today.getTime();
        });
        
        const incompleteTodayTasks = todayTasks.filter(t => 
          t.status !== 'done' && t.id !== task.id
        );
        
        if (todayTasks.length > 0 && incompleteTodayTasks.length === 0) {
          setTimeout(() => {
            toast.success("Nice, you've completed all tasks for today! 🎉🎊", {
              duration: 5000,
              icon: '🌟',
              style: {
                background: '#006D77',
                color: '#fff',
                fontWeight: 'bold'
              }
            });
          }, 500);
        }
      } else {
        toast.success('Task reopened!');
      }
      
      refetch();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = (task) => {
    setTaskToDelete(task);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);
    try {
      await removeTask(taskToDelete.listId, taskToDelete.id);
      toast.success('Task deleted');
      refetch();
      setShowDeleteModal(false);
      setTaskToDelete(null);
    } catch (error) {
      toast.error('Failed to delete task');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTaskToDelete(null);
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const handleArchive = async (task) => {
    try {
      // Debug: Log task details
      console.log('[Archive] Task:', task.id, 'listId:', task.listId, 'status:', task.status);
      
      if (!task.listId) {
        console.error('[Archive] Missing listId for task:', task);
        toast.error('Cannot archive: Missing list information');
        return;
      }
      
      await archiveTaskItem(task.listId, task.id);
      
      // Refetch to ensure proper sync
      refetch();
      
      // Clear any existing undo action
      if (undoAction?.timeoutId) {
        clearTimeout(undoAction.timeoutId);
      }
      
      const timeoutId = setTimeout(() => {
        setUndoAction(null);
      }, 5000);
      
      setUndoAction({ type: 'archive', task, timeoutId });
      
      toast((t) => (
        <div className="flex items-center gap-3">
          <span>Task archived</span>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await unarchiveTaskItem(task.listId, task.id);
                toast.success('Task restored!');
                refetch();
              } catch (error) {
                toast.error('Failed to undo');
              }
              setUndoAction(null);
            }}
            className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
          >
            Undo
          </button>
        </div>
      ), {
        duration: 5000,
        style: {
          background: '#6b7280',
          color: '#fff',
        }
      });
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  const handleUnarchive = async (task) => {
    try {
      await unarchiveTaskItem(task.listId, task.id);
      refetch();
      toast.success('Task unarchived');
    } catch (error) {
      console.error('Error unarchiving task:', error);
      toast.error('Failed to unarchive task');
    }
  };

  // Phase 8.7: Quick inline edit handler
  const handleQuickEdit = async (task, updates) => {
    try {
      await editTask(task.listId, task.id, updates);
      refetch();
    } catch (error) {
      console.error('Error quick editing task:', error);
      throw error; // Re-throw so TaskCard can show error
    }
  };

  const handleTaskClick = (task) => {
    setTaskToView(task);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setTaskToView(null);
  };

  const handleSaveEdit = async (updatedTask) => {
    try {
      await editTask(updatedTask.listId, updatedTask.id, updatedTask);
      toast.success('Task updated!');
      refetch();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleAddTask = async (taskData) => {
    await addTask(taskData);
    refetch();
  };

  // Phase 7: Bulk selection handlers
  const toggleTaskSelection = useCallback((taskId) => {
    setSelectedTaskIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  }, []);

  const selectAllTasks = useCallback(() => {
    const allIds = sortedTasks.map(t => t.id);
    setSelectedTaskIds(new Set(allIds));
  }, [sortedTasks]);

  const clearSelection = useCallback(() => {
    setSelectedTaskIds(new Set());
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedTaskIds(new Set());
  }, []);

  const handleBulkComplete = async () => {
    if (selectedTaskIds.size === 0) return;
    
    try {
      const tasksToComplete = sortedTasks.filter(t => 
        selectedTaskIds.has(t.id) && t.status !== 'done'
      );
      
      await Promise.all(
        tasksToComplete.map(task => toggleTask(task.listId, task.id))
      );
      
      toast.success(`${tasksToComplete.length} tasks completed`);
      exitSelectMode();
      refetch();
    } catch (error) {
      toast.error('Failed to complete tasks');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedTaskIds.size === 0) return;
    
    try {
      const tasksToArchive = sortedTasks.filter(t => 
        selectedTaskIds.has(t.id) && t.status !== 'archived'
      );
      
      await Promise.all(
        tasksToArchive.map(task => archiveTaskItem(task.listId, task.id))
      );
      
      toast.success(`${tasksToArchive.length} tasks archived`);
      exitSelectMode();
      refetch();
    } catch (error) {
      toast.error('Failed to archive tasks');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.size === 0) return;
    
    const count = selectedTaskIds.size;
    const confirmed = window.confirm(`Delete ${count} task${count > 1 ? 's' : ''}? This cannot be undone.`);
    
    if (!confirmed) return;
    
    try {
      const tasksToDelete = sortedTasks.filter(t => selectedTaskIds.has(t.id));
      
      await Promise.all(
        tasksToDelete.map(task => removeTask(task.listId, task.id))
      );
      
      toast.success(`${count} tasks deleted`);
      exitSelectMode();
      refetch();
    } catch (error) {
      toast.error('Failed to delete tasks');
    }
  };

  // Phase 7: Check if any modal is open (for keyboard shortcuts)
  const isAnyModalOpen = showAddModal || showEditModal || taskToDelete !== null || 
                         selectedTask !== null || showTemplatesModal || showRecordTransactionModal ||
                         showCalendarPopover;
  
  // Phase 7: Keyboard shortcuts hook
  const { showShortcutsModal, setShowShortcutsModal } = useKeyboardShortcuts({
    onNewTask: () => setShowAddModal(true),
    onEscape: () => {
      // Close modals or exit modes
      if (isSelectMode) {
        exitSelectMode();
      } else if (isDragMode) {
        setIsDragMode(false);
      }
    },
    onFilterChange: setActiveFilter,
    onToggleReorderMode: () => {
      if (!isSelectMode && viewMode === 'list') {
        setIsDragMode(prev => !prev);
      }
    },
    onToggleSelectMode: () => {
      if (!isDragMode && viewMode === 'list') {
        setIsSelectMode(prev => {
          if (prev) setSelectedTaskIds(new Set());
          return !prev;
        });
      }
    },
    onSelectAll: selectAllTasks,
    onDeleteSelected: handleBulkDelete,
    onCompleteSelected: handleBulkComplete,
    onArchiveSelected: handleBulkArchive,
    isSelectMode,
    isModalOpen: isAnyModalOpen,
    enabled: viewMode === 'list',
  });

  // Phase 3: Render task card with swipe support on touch devices
  const renderTaskCard = (task) => {
    const CardComponent = isTouchDevice ? SwipeableTaskCard : TaskCard;
    return (
      <CardComponent
        key={task.id}
        task={task}
        onToggle={handleToggle}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onClick={handleTaskClick}
        onQuickEdit={handleQuickEdit}
        enableSwipe={isTouchDevice}
      />
    );
  };

  // Summaries for AI endpoints
  const taskSummaries = useMemo(() => tasks.map(t => ({
    id: t.id,
    title: t.title,
    priority: t.priority,
    isFinancialTask: t.isFinancialTask,
    dueDate: t.dueDate || null
  })), [tasks]);

  async function requestAIOrdering(timeWindow = 'today') {
    if (!user?.uid) return;
    
    // FINLIST: Show friendly panel with guidance when no tasks exist
    if (taskSummaries.length === 0) {
      setAiOrdering([]); // Clear any existing ordering
      setShowAIOrderingPanel(true); // Show panel with helpful message
      return;
    }
    
    setAiOrderingLoading(true);
    console.log('[AI-HOOK] FinTasks', { action: 'prioritise', timeWindow, tasks: taskSummaries.length });
    try {
      const data = await prioritiseTasks({ userId: user.uid, tasks: taskSummaries, timeWindow });
      setAiOrdering(data.ordered);
      setShowAIOrderingPanel(true);
    } catch (e) {
      toast.error('Failed to get AI prioritisation');
      console.error(e);
    } finally {
      setAiOrderingLoading(false);
    }
  }

  async function requestSpendingDrafts() {
    if (!user?.uid) return;
    setSpendingDraftsLoading(true);
    console.log('[AI-HOOK] FinTasks', { action: 'from-spending' });
    try {
      const data = await tasksFromSpending({ userId: user.uid });
      setSpendingDrafts(data.drafts || []);
      setShowSpendingDraftsPanel(true);
    } catch (e) {
      console.error(e);
      // FINLIST: Show a friendly panel with guidance instead of harsh error toast
      setSpendingDrafts([]); // Clear any existing drafts
      setShowSpendingDraftsPanel(true); // Show the panel with helpful message
    } finally {
      setSpendingDraftsLoading(false);
    }
  }

  function applyAIOrderingLocally() {
    setUseAIOrderView(true);
    setShowAIOrderingPanel(false); // Close the panel to show the reordered list
    toast.success('AI ordering applied! Tasks are now shown in priority order.');
    console.log('[AI-HOOK] FinTasks', { action: 'apply-local-ordering', count: aiOrdering.length });
  }

  function clearAIOrdering() {
    setUseAIOrderView(false);
    setAiOrdering([]);
    setShowAIOrderingPanel(false);
    console.log('[AI-HOOK] FinTasks', { action: 'dismiss-ordering' });
  }

  // Handle creating tasks from template
  const handleCreateTemplate = async (tasks, templateName) => {
    try {
      let successCount = 0;
      for (const task of tasks) {
        try {
          await addTask(task);
          successCount++;
        } catch (error) {
          console.error('Error creating task:', error);
        }
      }
      
      if (successCount === tasks.length) {
        toast.success(`Created all ${tasks.length} tasks from "${templateName}"! 🎉`);
      } else if (successCount > 0) {
        toast.success(`Created ${successCount} of ${tasks.length} tasks from "${templateName}"`);
      } else {
        toast.error('Failed to create tasks from template');
      }
      
      refetch();
    } catch (error) {
      console.error('Error creating template tasks:', error);
      toast.error('Failed to create tasks from template');
    }
  };

  // Group tasks by time buckets for "All Tasks" view
  const groupTasksByTime = (tasks) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    const groups = {
      today: [],
      thisWeek: [],
      later: []
    };

    tasks.forEach(task => {
      if (!task.dueDate) {
        groups.later.push(task);
        return;
      }

      const dueDate = new Date(task.dueDate);
      dueDate.setHours(0, 0, 0, 0);

      if (dueDate.getTime() === today.getTime()) {
        groups.today.push(task);
      } else if (dueDate > today && dueDate <= nextWeek) {
        groups.thisWeek.push(task);
      } else {
        groups.later.push(task);
      }
    });

    return groups;
  };

  const taskGroups = activeFilter === 'all' ? groupTasksByTime(sortedTasks) : null;

  // Test notification function
  const sendTestNotification = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('Please sign in to test notifications');
        return;
      }
      
      const token = await currentUser.getIdToken();
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
      
      const response = await fetch(`${apiBase}/push/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.sent > 0) {
          toast.success('Test notification sent! Check your notifications.');
        } else {
          toast.error('No notifications sent. Please enable notifications first.');
        }
      } else {
        toast.error(data.message || data.error || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Test notification error:', error);
      toast.error('Failed to send test notification');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFB] dark:bg-slate-900">
      <main className="px-3 sm:px-4 md:px-6 py-4 sm:py-6 pb-20 sm:pb-24 max-w-7xl mx-auto">
        {/* Notification Permission Banner */}
        <NotificationPermissionBanner />

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                FinTasks
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Manage your personal and financial to-dos in one place
              </p>
            </div>
            {/* Test Notification Button - only show when notifications might be enabled */}
            {typeof Notification !== 'undefined' && Notification.permission === 'granted' && (
              <button
                onClick={sendTestNotification}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-lg border border-purple-200 dark:border-purple-700 transition-all"
                title="Send a test push notification"
              >
                <Bell size={14} />
                Test
              </button>
            )}
          </div>
        </motion.div>

        {/* Stats Cards - Collapsible Section */}
        {!statsLoading && stats && (
          <div className="mb-6">
            {/* Toggle Header */}
            <button
              onClick={() => setShowStatsPanel(!showStatsPanel)}
              className="w-full flex items-center justify-between px-3 py-2 mb-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <ListTodo size={16} className="text-[#006D77]" />
                Task Overview
              </span>
              <motion.span
                animate={{ rotate: showStatsPanel ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown size={18} className="text-gray-500 dark:text-gray-400" />
              </motion.span>
            </button>

            {/* Collapsible Content */}
            <AnimatePresence>
              {showStatsPanel && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Total Tasks Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/60 dark:from-emerald-900/30 dark:via-slate-800 dark:to-teal-900/30 rounded-xl p-3 border-l-4 border-l-emerald-500 border border-emerald-100 dark:border-emerald-800 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                  <ListTodo size={18} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Tasks</p>
                </div>
              </div>
            </motion.div>

            {/* Upcoming Payments Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-gradient-to-br from-rose-50/80 via-white to-pink-50/60 dark:from-rose-900/30 dark:via-slate-800 dark:to-pink-900/30 rounded-xl p-3 border-l-4 border-l-rose-500 border border-rose-100 dark:border-rose-800 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-rose-100 dark:bg-rose-900/50 flex items-center justify-center flex-shrink-0">
                  <Wallet size={18} className="text-rose-600 dark:text-rose-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-gray-900 dark:text-white truncate">
                    {formatCurrency(stats.financialAmount || 0, currency)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Payments</p>
                </div>
              </div>
              {stats.financial > 0 && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 truncate">{stats.financial} money tasks</p>
              )}
            </motion.div>

            {/* Due Today Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50/80 via-white to-indigo-50/60 dark:from-blue-900/30 dark:via-slate-800 dark:to-indigo-900/30 rounded-xl p-3 border-l-4 border-l-blue-500 border border-blue-100 dark:border-blue-800 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                  <Calendar size={18} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.today}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Due Today</p>
                </div>
              </div>
            </motion.div>

            {/* Overdue Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-gradient-to-br from-red-50/80 via-white to-orange-50/60 dark:from-red-900/30 dark:via-slate-800 dark:to-orange-900/30 rounded-xl p-3 border-l-4 border-l-red-500 border border-red-100 dark:border-red-800 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={18} className="text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.overdue}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Overdue</p>
                </div>
              </div>
            </motion.div>

            {/* Completion Rate Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-purple-50/80 via-white to-violet-50/60 dark:from-purple-900/30 dark:via-slate-800 dark:to-violet-900/30 rounded-xl p-3 border-l-4 border-l-purple-500 border border-purple-100 dark:border-purple-800 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={18} className="text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.completionRate}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Complete</p>
                </div>
              </div>
              {stats.streak > 0 && (
                <p className="text-[10px] text-orange-600 dark:text-orange-400 font-medium mt-1.5">🔥 {stats.streak}d streak</p>
              )}
            </motion.div>

            {/* FINLIST: Budget-Linked Tasks Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-gradient-to-br from-teal-50/80 via-white to-cyan-50/60 dark:from-teal-900/30 dark:via-slate-800 dark:to-cyan-900/30 rounded-xl p-3 border-l-4 border-l-teal-500 border border-teal-100 dark:border-teal-800 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0">
                  <LinkIcon size={18} className="text-teal-600 dark:text-teal-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-gray-900 dark:text-white truncate">
                    {formatCurrency(budgetLinkedMetrics.totalBudgetImpact, currency)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Budget</p>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 truncate">
                {budgetLinkedMetrics.linkedCount > 0 
                  ? `${budgetLinkedMetrics.linkedCount} linked` 
                  : 'No links yet'}
              </p>
            </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Add Custom Task Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowAddModal(true)}
          className="w-full mb-4 py-3 px-4 bg-gradient-to-r from-[#006D77] to-[#118AB2] text-white rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold hover:shadow-xl transition-all"
        >
          <Plus size={20} />
          Add Custom Task
        </motion.button>

        {/* Phase 5: Quick Add Templates - Horizontal scrolling widget */}
        <QuickAddTemplates 
          onAddTemplate={async (template, tasks) => {
            // Add all tasks from template
            for (const task of tasks) {
              await handleAddTask(task);
            }
          }}
          onViewAll={() => setShowTemplatesModal(true)}
          listId={defaultListId}
        />

        {/* Phase 5: AI-Generated Suggested Tasks */}
        <SuggestedTasksWidget
          onAddSuggestion={handleAddTask}
          listId={defaultListId}
          currencySymbol={currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€'}
        />

        {/* Filters + View Toggle */}
        <div className="mb-6">
          <div className="flex items-center flex-wrap gap-3 mb-3">
            {/* View Mode Toggle + Action Buttons */}
            <div className="flex items-center gap-1 p-1 bg-gray-100/80 dark:bg-slate-800 rounded-xl backdrop-blur-sm">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  viewMode === 'list'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 border-2 border-teal-400 scale-105'
                    : 'text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 border-2 border-transparent hover:border-teal-300 dark:hover:border-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30'
                }`}
              >
                <LayoutList size={16} />
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  viewMode === 'calendar'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/30 border-2 border-indigo-400 scale-105'
                    : 'text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 border-2 border-transparent hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                }`}
              >
                <CalendarDays size={16} />
                Calendar
              </button>
              
              {/* Separator */}
              {viewMode === 'list' && !isSelectMode && (
                <div className="h-6 w-px bg-gray-300 dark:bg-slate-600 mx-1" />
              )}
              
              {/* Reorder & Select - only in list view */}
              {viewMode === 'list' && !isSelectMode && (
                <>
                  <button
                    onClick={() => setIsDragMode(!isDragMode)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      isDragMode
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30 border-2 border-purple-400 scale-105'
                        : 'text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 border-2 border-transparent hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                    }`}
                  >
                    <GripVertical size={16} />
                    {isDragMode ? 'Done' : 'Reorder'}
                  </button>
                  <button
                    onClick={() => setIsSelectMode(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 border-2 border-transparent hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all duration-300"
                  >
                    <CheckSquare size={16} />
                    Select
                  </button>
                </>
              )}
            </div>
            
            {/* AI Buttons: Prioritise & Suggest */}
            {viewMode === 'list' && !isSelectMode && (
              <div className="flex items-center gap-2">
                {tasks.length > 0 && (
                  <button
                    type="button"
                    onClick={() => requestAIOrdering(activeFilter === 'today' ? 'today' : 'week')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-sm transition-all duration-300 border-2 bg-white dark:bg-slate-800 text-[#006D77] dark:text-teal-400 border-[#006D77] dark:border-teal-600 hover:bg-gradient-to-r hover:from-[#006D77] hover:to-teal-500 hover:text-white hover:shadow-lg hover:shadow-teal-500/30"
                    aria-label="AI prioritise tasks"
                  >
                    {aiOrderingLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {aiOrderingLoading ? 'Prioritising…' : 'Prioritise'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={requestSpendingDrafts}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-sm transition-all duration-300 border-2 bg-white dark:bg-slate-800 text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-600 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/30"
                  aria-label="AI suggest tasks"
                >
                  {spendingDraftsLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Suggest
                </button>
              </div>
            )}
            
            {/* Keyboard Shortcuts Button */}
            {viewMode === 'list' && !isSelectMode && (
              <button
                onClick={() => setShowShortcutsModal(true)}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-300 dark:hover:border-amber-600 border-2 border-transparent transition-all duration-300"
                title="Keyboard shortcuts (?)"
              >
                <Keyboard size={16} />
              </button>
            )}
            
            {/* Bulk Actions Bar - shown when in select mode */}
            {viewMode === 'list' && isSelectMode && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {selectedTaskIds.size} selected
                </span>
                <button
                  onClick={selectAllTasks}
                  className="px-2 py-1 text-xs font-medium text-[#006D77] dark:text-teal-400 hover:bg-[#006D77]/10 dark:hover:bg-teal-900/30 rounded transition-all"
                >
                  All
                </button>
                <button
                  onClick={clearSelection}
                  className="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-all"
                >
                  None
                </button>
                <div className="h-4 w-px bg-gray-300 dark:bg-slate-600 mx-1" />
                <button
                  onClick={handleBulkComplete}
                  disabled={selectedTaskIds.size === 0}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-all disabled:opacity-50"
                >
                  <CheckCircle2 size={14} />
                  Complete
                </button>
                <button
                  onClick={handleBulkArchive}
                  disabled={selectedTaskIds.size === 0}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-all disabled:opacity-50"
                >
                  <Archive size={14} />
                  Archive
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={selectedTaskIds.size === 0}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-all disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
                <div className="h-4 w-px bg-gray-300 dark:bg-slate-600 mx-1" />
                <button
                  onClick={exitSelectMode}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-all"
                >
                  <X size={14} />
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {/* Phase 8.1: Search Bar & Phase 8.5: Sort Options */}
          {viewMode === 'list' && (
          <div className="flex flex-wrap items-center gap-3 mb-3" role="search" aria-label="Task search and sort controls">
            {/* Search Bar */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search tasks by title, description, or tags"
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-[#006D77] dark:focus:border-teal-500 focus:ring-2 focus:ring-[#006D77]/20 dark:focus:ring-teal-500/20 outline-none transition-all duration-300 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            
            {/* Sort Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowSortDropdown(!showSortDropdown);
                }}
                aria-expanded={showSortDropdown}
                aria-haspopup="listbox"
                aria-label={`Sort tasks by ${sortBy === 'default' ? 'default order' : sortBy}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all duration-300 ${
                  sortBy !== 'default'
                    ? 'bg-[#006D77] dark:bg-teal-600 text-white border-[#006D77] dark:border-teal-600'
                    : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500'
                }`}
              >
                <ArrowUpDown size={16} aria-hidden="true" />
                Sort
              </button>
              
              {showSortDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowSortDropdown(false)} 
                  />
                  <div 
                    role="listbox"
                    aria-label="Sort options"
                    className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-lg border-2 border-gray-100 dark:border-slate-700 py-2 z-20"
                  >
                    {[
                      { id: 'default', label: 'Default' },
                      { id: 'dueDate', label: 'Due Date' },
                      { id: 'priority', label: 'Priority' },
                      { id: 'created', label: 'Newest First' },
                      { id: 'alphabetical', label: 'A-Z' }
                    ].map((option) => (
                      <button
                        key={option.id}
                        role="option"
                        aria-selected={sortBy === option.id}
                        onClick={() => {
                          setSortBy(option.id);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          sortBy === option.id
                            ? 'bg-[#006D77]/10 dark:bg-teal-900/30 text-[#006D77] dark:text-teal-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Phase 8.4: Daily Progress Ring */}
            {todayProgress.total > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-xl border-2 border-emerald-200 dark:border-emerald-700">
                <div className="relative w-8 h-8">
                  <svg className="w-8 h-8 -rotate-90" viewBox="0 0 36 36">
                    <circle
                      cx="18" cy="18" r="15"
                      fill="none"
                      stroke="#e5e7eb"
                      className="dark:stroke-slate-600"
                      strokeWidth="3"
                    />
                    <circle
                      cx="18" cy="18" r="15"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray={`${todayProgress.percentage * 0.94} 94`}
                      className="transition-all duration-500"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-emerald-600 dark:text-emerald-400">
                    {todayProgress.percentage}%
                  </span>
                </div>
                <div className="text-xs">
                  <p className="font-medium text-gray-700 dark:text-gray-300">Today</p>
                  <p className="text-gray-500 dark:text-gray-400">{todayProgress.completed}/{todayProgress.total}</p>
                </div>
              </div>
            )}
          </div>
          )}
          
          {/* Filter Pills (only show in list mode) */}
          {viewMode === 'list' && (
          <div className="pb-2">
            {/* Mobile: Horizontal scrollable chips */}
            <div 
              ref={filterScrollRef}
              className="flex sm:flex-wrap items-center gap-2 overflow-x-auto sm:overflow-visible pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide"
              role="tablist"
              aria-label="Task filters"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filters.map((filter) => {
                const Icon = filter.icon;
                const count = filterCounts[filter.id] || 0;
                return (
                  <button
                    key={filter.id}
                    role="tab"
                    aria-selected={activeFilter === filter.id}
                    aria-label={`${filter.label}${count > 0 ? `, ${count} tasks` : ''}`}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`
                      flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 border-2 whitespace-nowrap flex-shrink-0
                      ${activeFilter === filter.id
                        ? `${filterColors[filter.id]?.active || filterColors.all.active} scale-105`
                        : filterColors[filter.id]?.inactive || filterColors.all.inactive
                      }
                    `}
                  >
                    <Icon size={12} className="sm:w-[14px] sm:h-[14px]" aria-hidden="true" />
                    <span className="hidden sm:inline">{filter.label}</span>
                    <span className="sm:hidden">{filter.shortLabel}</span>
                    {count > 0 && (
                      <span 
                        aria-hidden="true"
                        className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
                          activeFilter === filter.id
                            ? 'bg-white/30 text-white'
                            : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Scroll Indicator Dots (mobile only) */}
            <div className="sm:hidden flex items-center justify-center gap-1.5 pt-1">
              <span 
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  showFilterLeftArrow 
                    ? 'bg-gray-300 dark:bg-slate-600' 
                    : 'bg-[#006D77] dark:bg-[#83C5BE] scale-125'
                }`}
              />
              <span 
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  showFilterLeftArrow && showFilterRightArrow 
                    ? 'bg-[#006D77] dark:bg-[#83C5BE] scale-125' 
                    : 'bg-gray-300 dark:bg-slate-600'
                }`}
              />
              <span 
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  showFilterRightArrow 
                    ? 'bg-gray-300 dark:bg-slate-600' 
                    : 'bg-[#006D77] dark:bg-[#83C5BE] scale-125'
                }`}
              />
            </div>
          </div>
          )}
        </div>

        {/* AI Prioritisation Panel */}
        {showAIOrderingPanel && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 border-2 border-[#006D77]/40 dark:border-teal-600/40 rounded-2xl p-4 mb-6"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Sparkles size={18} className="text-[#006D77] dark:text-teal-400" /> AI suggested order
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Preview only. No tasks are changed automatically.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-[#006D77]/10 dark:bg-teal-900/30 rounded text-[8px] font-bold text-[#006D77] dark:text-teal-400">85</span>
                  = Priority score (higher = more urgent)
                </p>
              </div>
              <div className="flex gap-2">
                {!useAIOrderView && aiOrdering.length > 0 && (
                  <button
                    onClick={applyAIOrderingLocally}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#006D77] dark:bg-teal-600 text-white hover:bg-[#006D77]/90 dark:hover:bg-teal-500"
                  >
                    Apply locally
                  </button>
                )}
                <button
                  onClick={clearAIOrdering}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
            {aiOrdering.length === 0 ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#006D77]/10 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                  <Sparkles size={28} className="text-[#006D77]/50 dark:text-teal-400/50" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">No tasks to prioritise yet</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
                  Add some tasks first, then let FinList help you prioritise them based on 
                  due dates, importance, and your financial goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={() => { setShowAIOrderingPanel(false); setShowAddModal(true); }}
                    className="px-4 py-2 text-sm font-semibold rounded-xl bg-[#006D77] dark:bg-teal-600 text-white hover:bg-[#006D77]/90 dark:hover:bg-teal-500 transition-colors"
                  >
                    + Create your first task
                  </button>
                  <button
                    onClick={() => setShowAIOrderingPanel(false)}
                    className="px-4 py-2 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            ) : (
              <ul className="space-y-2">
                {aiOrdering.map((o, idx) => {
                  const task = tasks.find(t => t.id === o.taskId);
                  if (!task) return null;
                  return (
                    <li key={o.taskId} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800/50">
                      <span className="text-xs font-bold text-gray-500 dark:text-gray-400 w-6">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{task.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{o.reason}</p>
                      </div>
                      <span 
                        className="text-[10px] font-semibold text-[#006D77] dark:text-teal-400 bg-[#006D77]/10 dark:bg-teal-900/30 px-2 py-1 rounded-lg cursor-help"
                        title={`Priority score: ${o.priorityScore}/100 - Higher means more urgent`}
                      >
                        {o.priorityScore}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}

        {/* Spending Draft Suggestions Panel */}
        {showSpendingDraftsPanel && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 border-2 border-purple-600/40 dark:border-purple-500/40 rounded-2xl p-4 mb-6"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Sparkles size={18} className="text-purple-600 dark:text-purple-400" /> Suggested tasks from spending
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Draft tasks only. You choose what to add.</p>
              </div>
              <button
                onClick={() => { setShowSpendingDraftsPanel(false); setSpendingDrafts([]); }}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                Dismiss
              </button>
            </div>
            {spendingDrafts.length === 0 ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-purple-600/10 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <Sparkles size={28} className="text-purple-600/50 dark:text-purple-400/50" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">No spending patterns found yet</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
                  FinList learns from your transactions to suggest smart financial tasks. 
                  Start by adding some expenses or connecting your accounts.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={() => { setShowSpendingDraftsPanel(false); setShowAddModal(true); }}
                    className="px-4 py-2 text-sm font-semibold rounded-xl bg-purple-600 text-white hover:bg-purple-600/90 transition-colors"
                  >
                    + Create a task manually
                  </button>
                  <button
                    onClick={() => { setShowSpendingDraftsPanel(false); window.location.href = '/budget'; }}
                    className="px-4 py-2 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    Go to Budget & Transactions
                  </button>
                </div>
              </div>
            ) : (
              <ul className="space-y-2">
                {spendingDrafts.map(d => (
                  <li key={d.id} className="p-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800/50 flex flex-col gap-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{d.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">{d.explanation}</p>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => handleAddTask({
                          title: d.title,
                          description: d.explanation,
                          isFinancialTask: true,
                          priority: 'medium',
                          listId: defaultListId,
                          tags: ['ai', d.category?.toLowerCase()].filter(Boolean)
                        })}
                        className="px-2.5 py-1.5 text-[11px] font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-600/90"
                      >
                        Add task
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}

        {/* Phase 6: Calendar View */}
        {viewMode === 'calendar' ? (
          <div className="mb-6">
            <CalendarView
              tasks={sortedTasks}
              onDateSelect={(date, dayTasks) => {
                setSelectedCalendarDate(date);
                setCalendarPopoverTasks(dayTasks);
                setShowCalendarPopover(true);
              }}
              onAddTask={(date) => {
                setSelectedCalendarDate(date);
                // Format date as YYYY-MM-DD for the date input
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setPrefilledTaskDate(`${year}-${month}-${day}`);
                setShowAddModal(true);
              }}
              selectedDate={selectedCalendarDate}
            />
          </div>
        ) : (
        <>
        {/* Tasks List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-[#006D77] dark:text-teal-400" />
          </div>
        ) : tasks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <ListTodo size={64} className="mx-auto text-gray-300 dark:text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {activeFilter === 'all' ? 'No tasks yet' : `No ${activeFilter} tasks`}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first task to get started
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-[#006D77] dark:bg-teal-600 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Create Your First Task
            </motion.button>
          </motion.div>
        ) : activeFilter === 'all' && taskGroups && !isDragMode && !isSelectMode && !useAIOrderView ? (
          <div className="space-y-8">
            {/* Today Section */}
            {taskGroups.today.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Calendar size={20} className="text-[#006D77] dark:text-teal-400" />
                  Today
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({taskGroups.today.length})</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {taskGroups.today.map((task) => renderTaskCard(task))}
                </div>
              </div>
            )}

            {/* This Week Section */}
            {taskGroups.thisWeek.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Calendar size={20} className="text-[#118AB2] dark:text-cyan-400" />
                  This Week
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({taskGroups.thisWeek.length})</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {taskGroups.thisWeek.map((task) => renderTaskCard(task))}
                </div>
              </div>
            )}

            {/* Later Section */}
            {taskGroups.later.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-gray-500 dark:text-gray-400" />
                  Later
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({taskGroups.later.length})</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {taskGroups.later.map((task) => renderTaskCard(task))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Phase 7: Use DraggableTaskList when in drag mode or select mode */
          <DraggableTaskList
            tasks={(useAIOrderView && aiOrdering.length > 0)
              ? aiOrdering.map(o => sortedTasks.find(t => t.id === o.taskId)).filter(Boolean)
              : sortedTasks
            }
            onReorder={async (newOrder) => {
              try {
                await reorderTasks(newOrder);
                toast.success('Tasks reordered');
              } catch (error) {
                toast.error('Failed to save order');
              }
            }}
            onToggle={handleToggle}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onClick={handleTaskClick}
            enableSwipe={isTouchDevice}
            isDragMode={isDragMode}
            isSelectMode={isSelectMode}
            selectedTasks={[...selectedTaskIds]}
            onToggleSelect={toggleTaskSelection}
          />
        )}
        
        {/* Bulk Action Bar - shows when tasks are selected */}
        <AnimatePresence>
          {isSelectMode && selectedTaskIds.size > 0 && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 px-4 py-3 flex items-center gap-4"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedTaskIds.size} selected
              </span>
              
              <div className="h-6 w-px bg-gray-200 dark:bg-slate-600" />
              
              <button
                onClick={selectAllTasks}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
              >
                Select All
              </button>
              
              <button
                onClick={clearSelection}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear
              </button>
              
              <div className="h-6 w-px bg-gray-200 dark:bg-slate-600" />
              
              <button
                onClick={handleBulkComplete}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors text-sm font-medium"
              >
                <CheckSquare size={16} />
                Done
              </button>
              
              <button
                onClick={handleBulkArchive}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors text-sm font-medium"
              >
                <Archive size={16} />
                Archive
              </button>
              
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-sm font-medium"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        </>
        )}

        {/* Calendar Popover */}
        <TaskCalendarPopover
          isOpen={showCalendarPopover}
          onClose={() => setShowCalendarPopover(false)}
          date={selectedCalendarDate}
          tasks={calendarPopoverTasks}
          onToggleTask={(task) => toggleTask(task.listId, task.id)}
          onAddTask={(date) => {
            setShowCalendarPopover(false);
            // Format date as YYYY-MM-DD for the date input
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            setPrefilledTaskDate(`${year}-${month}-${day}`);
            setShowAddModal(true);
          }}
          onTaskClick={(task) => {
            setShowCalendarPopover(false);
            setTaskToView(task);
            setShowDetailModal(true);
          }}
          currencySymbol={currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€'}
        />
      </main>

      <BottomNavigation />

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setPrefilledTaskDate(null); // Clear prefilled date when closing
        }}
        onSubmit={handleAddTask}
        prefilledData={prefilledTaskDate ? { dueDate: prefilledTaskDate } : {}}
      />

      {/* Edit Task Modal */}
      <EditTaskModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTask(null);
        }}
        onSave={handleSaveEdit}
        task={selectedTask}
      />

      {/* Task Templates Modal */}
      <TaskTemplatesModal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        onCreateTemplate={handleCreateTemplate}
        currentListId={defaultListId}
      />

      {/* Delete Task Confirmation Modal */}
      <DeleteTaskConfirmModal
        task={taskToDelete}
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isProcessing={isDeleting}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={taskToView}
        isOpen={showDetailModal}
        onClose={handleCloseDetail}
      />

      {/* Record Transaction Modal - Financial Task → Budget Integration */}
      <RecordTransactionModal
        isOpen={showRecordTransactionModal}
        onClose={() => {
          setShowRecordTransactionModal(false);
          setTaskToRecord(null);
        }}
        task={taskToRecord}
        onSuccess={() => {
          // Optionally refresh envelopes after recording
          refreshEnvelopes?.();
        }}
      />
      
      {/* Phase 7: Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />
    </div>
  );
}
